import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { z } from 'zod';
import type { addFacebookPageValidator } from '../validation';
import { isAxiosError } from 'axios';

type FacebookPagePayload = z.infer<typeof addFacebookPageValidator.schema>;

interface FacebookPagePayloadWithId extends FacebookPagePayload {
  id: number;
}

export interface AddFacebookPageResponse {
  message: string;
  facebook_page_id: number;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const addFacebookPage = async (
  params: FacebookPagePayload,
): Promise<AddFacebookPageResponse> => {
  const res = await apiClient.post<AddFacebookPageResponse>(
    queryKeys.facebookPages,
    params,
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add facebook page');
  }

  return res.data;
};

type MutationFnType = (
  data: FacebookPagePayload,
) => ReturnType<typeof addFacebookPage>;

interface UseOptions {
  config?: MutationConfig<MutationFnType>;
}

export const useAddFacebookPage = ({ config }: UseOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.facebookPages],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: addFacebookPage,
  });
};

export const updateFacebookPage = async ({
  id,
  ...params
}: FacebookPagePayloadWithId): Promise<AddFacebookPageResponse> => {
  const res = await apiClient.put<AddFacebookPageResponse>(
    `${queryKeys.facebookPages}/${id}`,
    params,
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update facebook page');
  }

  return res.data;
};

type UpdateMutationFnType = (
  data: FacebookPagePayloadWithId,
) => ReturnType<typeof updateFacebookPage>;

interface UseUpdateOptions {
  config?: MutationConfig<UpdateMutationFnType>;
}

export const useUpdateFacebookPage = ({ config }: UseUpdateOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.facebookPages],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.facebookPageDetails],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateFacebookPage,
  });
};
